import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { BookOpen, Globe, Building2, TrendingUp, Shield, Unlock, AlertTriangle, ArrowRight, BarChart3, GraduationCap, Sparkles, Users } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend } from "recharts";
import { AICCIMethodologyLink } from "../dashboard/AICCIMethodology";
import { InternationalBodies } from "./InternationalBodies";
import { DashboardSkeleton, Shimmer } from "../common/DashboardSkeleton";
import { usePrefetchOrFetch } from "../common/DataPrefetch";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const ORIENT_COLORS = { care: "#10b981", utility: "#3b82f6", creative: "#a855f7", extraction: "#ef4444", unclassified: "#475569" };

/* ─── Animated Counter ─── */
const Counter = ({ value, suffix = "" }) => {
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    if (!value) return;
    const target = typeof value === "string" ? parseFloat(value) : value;
    if (isNaN(target)) { setDisplay(value); return; }
    const startTime = performance.now();
    const animate = (now) => {
      const progress = Math.min((now - startTime) / 1200, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(progress < 1 ? Math.round(eased * target) : target);
      if (progress < 1) ref.current = requestAnimationFrame(animate);
    };
    ref.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(ref.current);
  }, [value]);
  return <>{typeof display === "number" ? (Number.isInteger(display) ? display.toLocaleString() : display.toFixed(1)) : display}{suffix}</>;
};

/* ─── Scroll Reveal ─── */
const Reveal = ({ children, delay = 0, className = "" }) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} className={className} style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(20px)", transition: `all 0.6s ease ${delay}ms` }}>
      {children}
    </div>
  );
};

/* ─── Stat Card ─── */
const StatCard = ({ icon: Icon, label, value, suffix, color, sub, delay = 0 }) => (
  <Reveal delay={delay} className="h-full">
    <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4 hover:border-white/[0.12] transition-all h-full">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4" style={{ color }} />
        <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color: `${color}90` }}>{label}</span>
      </div>
      <p className="text-2xl font-black text-white"><Counter value={value} suffix={suffix} /></p>
      {sub && <p className="text-[10px] text-slate-500 mt-1">{sub}</p>}
    </div>
  </Reveal>
);

/* ─── Research-Product Gap Chart ─── */
const GapChart = ({ gaps }) => {
  if (!gaps?.length) return null;
  const data = gaps.slice(0, 12).map(g => ({
    country: g.country,
    research: g.research_care_pct,
    product: g.product_care_pct,
    gap: g.gap,
  }));
  return (
    <Reveal delay={200}>
      <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <ArrowRight className="w-4 h-4 text-violet-400" />
          <p className="text-[10px] font-bold text-violet-400/70 uppercase tracking-[0.2em]">Research-to-Product Care Gap by Country</p>
        </div>
        <p className="text-[10px] text-slate-500 mb-4">Positive gap = research is more care-oriented than products. Negative = products are more care-oriented than research.</p>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data} layout="vertical" margin={{ left: 80, right: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
            <XAxis type="number" tick={{ fill: "#64748b", fontSize: 10 }} domain={[-30, 60]} />
            <YAxis type="category" dataKey="country" tick={{ fill: "#94a3b8", fontSize: 10 }} width={75} />
            <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 11 }} />
            <Bar dataKey="gap" fill="#8b5cf6" radius={[0, 4, 4, 0]} name="Care Gap (research - product)" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Reveal>
  );
};


/* ─── Section Skeleton ─── */
const SectionSkeleton = ({ rows = 3, height = "h-48" }) => (
  <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5 animate-pulse" data-testid="section-skeleton">
    <Shimmer className="h-3 w-40 mb-4" />
    <div className={`${height} flex items-end gap-3 px-4`}>
      {[65, 50, 70, 45, 55, 60, 40].map((h, j) => (
        <Shimmer key={j} className="flex-1 rounded" style={{ height: `${h}%` }} />
      ))}
    </div>
  </div>
);

/* ─── Main Export ─── */
export const ResearchObservatory = () => {
  const [stats, statsLoading] = usePrefetchOrFetch("researchStats", `${API}/research/stats`);
  const [cii, ciiLoading] = usePrefetchOrFetch("researchCii", `${API}/research/cii`);
  const [gaps, gapsLoading] = usePrefetchOrFetch("researchGap", `${API}/research/gap-analysis`, { method: "post" });

  // Show full skeleton only when nothing has loaded yet
  if (statsLoading && !stats) {
    return <DashboardSkeleton galaxyColor="cyan" />;
  }

  if (!stats || stats.total === 0) {
    return (
      <div className="text-center py-20" data-testid="research-observatory">
        <GraduationCap className="w-12 h-12 text-cyan-400/30 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-white mb-2">Research Observatory</h2>
        <p className="text-sm text-slate-400">Data ingestion in progress. Papers are being scraped from Semantic Scholar and arXiv.</p>
        <p className="text-[10px] text-slate-500 mt-2">Refresh in a few minutes to see the dashboard populate.</p>
      </div>
    );
  }

  const orientData = Object.entries(stats.orientation || {}).filter(([k]) => k !== "unclassified").map(([k, v]) => ({
    name: k.charAt(0).toUpperCase() + k.slice(1), value: v, color: ORIENT_COLORS[k]
  }));

  const yearData = (stats.years || []).filter(y => y.year <= new Date().getFullYear()).map(y => ({
    year: y.year, count: y.count,
    ...(stats.orient_by_year?.[y.year] || {}),
  }));

  return (
    <div className="space-y-6" data-testid="research-observatory">
      {/* Hero */}
      <Reveal>
        <div className="relative overflow-hidden rounded-2xl py-10 px-6 md:px-10 text-center"
          style={{ background: "linear-gradient(135deg, #0a1628 0%, #0c1220 40%, #0f1629 100%)", border: "1px solid rgba(6,182,212,0.15)" }}>
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-32 bg-cyan-500/[0.04] rounded-full blur-3xl" />
          <p className="text-[10px] font-bold text-cyan-400/70 uppercase tracking-[0.25em] mb-3">Galaxy 5 — Research Observatory</p>
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-3">The Consciousness of AI Research</h1>
          <p className="text-sm text-slate-400 max-w-2xl mx-auto">
            Tracking <span className="text-cyan-400 font-bold">{stats.total.toLocaleString()}</span> research papers from 7 sources: OpenAlex, Semantic Scholar, arXiv, HuggingFace, SSRN, Stanford HAI, and OECD.
            {stats.classified > 0 && <> <span className="text-emerald-400 font-bold">{stats.classified.toLocaleString()}</span> classified by value orientation.</>}
          </p>
          {cii?.score > 0 ? (
            <div className="mt-5 inline-flex items-center gap-3 px-5 py-2.5 bg-cyan-500/[0.08] border border-cyan-500/20 rounded-full">
              <span className="text-[10px] text-cyan-400/70 font-bold uppercase">Research CII</span>
              <span className="text-2xl font-black text-cyan-400">{cii.score}</span>
              <span className="text-[10px] text-slate-500">/ 100</span>
              <AICCIMethodologyLink />
            </div>
          ) : ciiLoading ? (
            <div className="mt-5 inline-flex items-center gap-3 px-5 py-2.5 bg-cyan-500/[0.04] border border-cyan-500/10 rounded-full animate-pulse">
              <span className="text-[10px] text-cyan-400/40 font-bold uppercase">Research CII</span>
              <Shimmer className="h-7 w-10 rounded" />
              <span className="text-[10px] text-slate-600">/ 100</span>
            </div>
          ) : null}
        </div>
      </Reveal>

      {/* Key Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard icon={BookOpen} label="Total Papers" value={stats.total} color="#06b6d4" delay={0} />
        <StatCard icon={Shield} label="Care-Oriented" value={stats.care_pct} suffix="%" color="#10b981" sub={`${stats.orientation?.care?.toLocaleString() || 0} papers`} delay={60} />
        <StatCard icon={AlertTriangle} label="Ethical Focus" value={stats.ethical_pct} suffix="%" color="#f59e0b" sub={`${stats.ethical_focus?.toLocaleString() || 0} papers`} delay={120} />
        <StatCard icon={Unlock} label="Open Access" value={stats.open_access_pct} suffix="%" color="#a855f7" sub={`${stats.open_access?.toLocaleString() || 0} papers`} delay={180} />
        <StatCard icon={Globe} label="Countries" value={stats.num_countries || stats.countries?.length} color="#6366f1" delay={240} />
        <StatCard icon={Building2} label="Institutions" value={stats.num_institutions || stats.institutions?.length || 0} color="#ec4899" delay={300} />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Orientation Donut */}
        {orientData.length > 0 && (
          <Reveal delay={100}>
            <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5">
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-[0.2em] mb-4">Value Orientation of AI Research</p>
              <div className="flex items-center gap-6">
                <ResponsiveContainer width="50%" height={200}>
                  <PieChart>
                    <Pie data={orientData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" stroke="none">
                      {orientData.map((entry) => <Cell key={entry.name} fill={entry.color} />)}
                    </Pie>
                    <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex-1 space-y-2">
                  {orientData.map((d) => (
                    <div key={d.name} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ background: d.color }} />
                        <span className="text-[10px] text-slate-300">{d.name}</span>
                      </div>
                      <span className="text-[10px] font-bold text-slate-400">{d.value.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        )}

        {/* Papers by Year */}
        {yearData.length > 0 && (
          <Reveal delay={150}>
            <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5">
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-[0.2em] mb-4">Research Papers by Year</p>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={yearData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                  <XAxis dataKey="year" tick={{ fill: "#64748b", fontSize: 10 }} />
                  <YAxis tick={{ fill: "#64748b", fontSize: 10 }} />
                  <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 11 }} />
                  <Bar dataKey="count" fill="#06b6d4" radius={[4, 4, 0, 0]} name="Papers" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Reveal>
        )}
      </div>

      {/* Orientation by Year (line chart) */}
      {Object.keys(stats.orient_by_year || {}).length > 0 && (
        <Reveal delay={200}>
          <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5">
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-[0.2em] mb-4">Value Orientation Trend Over Time</p>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={yearData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                <XAxis dataKey="year" tick={{ fill: "#64748b", fontSize: 10 }} />
                <YAxis tick={{ fill: "#64748b", fontSize: 10 }} />
                <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 11 }} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Line type="monotone" dataKey="care" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} name="Care" />
                <Line type="monotone" dataKey="utility" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} name="Utility" />
                <Line type="monotone" dataKey="creative" stroke="#a855f7" strokeWidth={2} dot={{ r: 3 }} name="Creative" />
                <Line type="monotone" dataKey="extraction" stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} name="Extraction" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Reveal>
      )}

      {/* Two-column: Top Countries + Top Institutions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {stats.countries?.length > 0 && (
          <Reveal delay={100}>
            <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <Globe className="w-4 h-4 text-indigo-400" />
                <p className="text-[10px] font-bold text-indigo-400/70 uppercase tracking-[0.2em]">Top Research Countries</p>
              </div>
              <div className="space-y-2">
                {stats.countries.map((c, i) => {
                  const maxCount = stats.countries[0]?.count || 1;
                  return (
                    <div key={c.country} className="flex items-center gap-3">
                      <span className="text-[10px] font-bold text-slate-500 w-4 text-right">{i + 1}</span>
                      <span className="text-[10px] text-slate-300 w-28 truncate">{c.country}</span>
                      <div className="flex-1 h-4 bg-white/[0.03] rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-1000"
                          style={{ width: `${(c.count / maxCount) * 100}%`, background: "linear-gradient(90deg, #6366f120, #6366f160)" }} />
                      </div>
                      <span className="text-[10px] font-bold text-slate-400 w-12 text-right">{c.count.toLocaleString()}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </Reveal>
        )}

        {stats.institutions?.length > 0 && (
          <Reveal delay={150}>
            <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <Building2 className="w-4 h-4 text-pink-400" />
                <p className="text-[10px] font-bold text-pink-400/70 uppercase tracking-[0.2em]">Top Research Institutions</p>
              </div>
              <div className="space-y-2">
                {stats.institutions.map((inst, i) => {
                  const maxCount = stats.institutions[0]?.count || 1;
                  return (
                    <div key={inst.institution} className="flex items-center gap-3">
                      <span className="text-[10px] font-bold text-slate-500 w-4 text-right">{i + 1}</span>
                      <span className="text-[10px] text-slate-300 w-40 truncate">{inst.institution}</span>
                      <div className="flex-1 h-4 bg-white/[0.03] rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-1000"
                          style={{ width: `${(inst.count / maxCount) * 100}%`, background: "linear-gradient(90deg, #ec489920, #ec489960)" }} />
                      </div>
                      <span className="text-[10px] font-bold text-slate-400 w-12 text-right">{inst.count.toLocaleString()}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </Reveal>
        )}
      </div>

      {/* Citations by Orientation */}
      {Object.keys(stats.citation_by_orient || {}).length > 0 && (
        <Reveal delay={100}>
          <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-4 h-4 text-amber-400" />
              <p className="text-[10px] font-bold text-amber-400/70 uppercase tracking-[0.2em]">Do Care Papers Get Cited?</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {Object.entries(stats.citation_by_orient).map(([orient, data]) => (
                <div key={orient} className="bg-white/[0.02] border border-white/[0.06] rounded-lg p-4 text-center">
                  <span className="w-3 h-3 rounded-full inline-block mb-2" style={{ background: ORIENT_COLORS[orient] }} />
                  <p className="text-lg font-black text-white">{data.avg}</p>
                  <p className="text-[9px] text-slate-500">avg citations</p>
                  <p className="text-[9px] text-slate-500 mt-1">{orient.charAt(0).toUpperCase() + orient.slice(1)}</p>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      )}

      {/* Top Cited Papers */}
      {stats.top_cited?.length > 0 && (
        <Reveal delay={150}>
          <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-4 h-4 text-cyan-400" />
              <p className="text-[10px] font-bold text-cyan-400/70 uppercase tracking-[0.2em]">Most Cited AI Papers</p>
            </div>
            <div className="space-y-2">
              {stats.top_cited.map((p, i) => (
                <div key={p.paper_title || `cited-${i}`} className="flex items-start gap-3 p-3 bg-white/[0.01] rounded-lg hover:bg-white/[0.03] transition-all">
                  <span className="text-lg font-black text-slate-600 w-6 text-right flex-shrink-0">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-white line-clamp-1">{p.paper_title}</p>
                    <div className="flex items-center gap-3 mt-1 text-[9px] text-slate-500">
                      {p.institution && p.institution !== "Unknown" && <span>{p.institution}</span>}
                      {p.country && p.country !== "Unknown" && <span>{p.country}</span>}
                      <span>{p.year}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="w-2 h-2 rounded-full" style={{ background: ORIENT_COLORS[p.value_orientation] || "#475569" }} />
                    <span className="text-sm font-black text-cyan-400">{p.citations?.toLocaleString()}</span>
                    <span className="text-[8px] text-slate-500">citations</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      )}

      {/* Author Composition */}
      {stats.author_composition && Object.keys(stats.author_composition).length > 0 && (
        <Reveal delay={200}>
          <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5" data-testid="author-composition-section">
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-4 h-4 text-pink-400" />
              <p className="text-[10px] font-bold text-pink-400/70 uppercase tracking-[0.2em]">Author Team Composition</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
              {[
                { key: "mixed", label: "Mixed Teams", color: "#8b5cf6", desc: "Male & female authors" },
                { key: "male_only", label: "All-Male Teams", color: "#3b82f6", desc: "Male authors only" },
                { key: "female_only", label: "All-Female Teams", color: "#ec4899", desc: "Female authors only" },
                { key: "unknown", label: "Undetected", color: "#64748b", desc: "Could not determine" },
              ].map(({ key, label, color, desc }) => {
                const count = stats.author_composition[key] || 0;
                const pct = stats.total > 0 ? ((count / stats.total) * 100).toFixed(1) : 0;
                return (
                  <div key={key} className="bg-white/[0.02] border border-white/[0.06] rounded-lg p-4 text-center" data-testid={`author-comp-${key}`}>
                    <div className="w-3 h-3 rounded-full mx-auto mb-2" style={{ background: color }} />
                    <p className="text-lg font-black text-white">{pct}%</p>
                    <p className="text-[9px] text-slate-400 font-medium">{label}</p>
                    <p className="text-[8px] text-slate-600 mt-0.5">{count.toLocaleString()} papers</p>
                  </div>
                );
              })}
            </div>

            {/* Composition × Orientation cross-tab */}
            {stats.author_composition_by_orient && Object.keys(stats.author_composition_by_orient).length > 0 && (
              <div>
                <p className="text-[9px] font-bold text-slate-500 uppercase tracking-[0.15em] mb-3">Composition × Orientation</p>
                <div className="space-y-2">
                  {["mixed", "female_only", "male_only"].map(comp => {
                    const data = stats.author_composition_by_orient[comp];
                    if (!data) return null;
                    const total = Object.values(data).reduce((s, v) => s + v, 0);
                    const carePct = total > 0 ? ((data.care || 0) / total * 100).toFixed(1) : 0;
                    const compLabel = comp === "mixed" ? "Mixed" : comp === "female_only" ? "All-Female" : "All-Male";
                    const compColor = comp === "mixed" ? "#8b5cf6" : comp === "female_only" ? "#ec4899" : "#3b82f6";
                    return (
                      <div key={comp} className="flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: compColor }} />
                        <span className="text-[10px] text-slate-300 w-20">{compLabel}</span>
                        <div className="flex-1 h-5 bg-white/[0.03] rounded-full overflow-hidden flex">
                          {["care", "utility", "creative", "extraction"].map(orient => {
                            const val = data[orient] || 0;
                            const pct = total > 0 ? (val / total * 100) : 0;
                            if (pct < 0.5) return null;
                            return (
                              <div key={orient} className="h-full transition-all duration-1000"
                                style={{ width: `${pct}%`, background: ORIENT_COLORS[orient] + "99" }}
                                title={`${orient}: ${val.toLocaleString()} (${pct.toFixed(1)}%)`} />
                            );
                          })}
                        </div>
                        <span className="text-[10px] font-bold w-14 text-right" style={{ color: "#10b981" }}>{carePct}% care</span>
                      </div>
                    );
                  })}
                </div>
                <p className="text-[8px] text-slate-600 mt-2">
                  Gender inferred from author first names. Teams with at least one male and one female author classified as "Mixed".
                </p>
              </div>
            )}
          </div>
        </Reveal>
      )}

      {/* Research-Product Gap */}
      {gaps?.gaps?.length > 0 ? <GapChart gaps={gaps.gaps} /> : gapsLoading && (
        <SectionSkeleton height="h-64" />
      )}

      {/* CII Dimensions */}
      {cii?.dimensions ? (
        <Reveal delay={200}>
          <div className="bg-cyan-500/[0.04] border border-cyan-500/12 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="w-4 h-4 text-cyan-400" />
              <p className="text-[10px] font-bold text-cyan-400/70 uppercase tracking-[0.2em]">Research CII Dimensions</p>
              <AICCIMethodologyLink />
              <span className="ml-auto text-xl font-black text-cyan-400">{cii.score}/100</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {Object.entries(cii.dimensions).map(([key, dim]) => (
                <div key={key} className="bg-white/[0.02] border border-white/[0.06] rounded-lg p-3">
                  <p className="text-[9px] font-bold text-slate-500 uppercase tracking-wider mb-1">{dim.label}</p>
                  <div className="flex items-end gap-2">
                    <span className="text-lg font-black text-white">{dim.score}</span>
                    <span className="text-[9px] text-slate-500 mb-0.5">/ 100</span>
                  </div>
                  <div className="w-full h-1.5 bg-white/[0.05] rounded-full mt-2">
                    <div className="h-full rounded-full bg-cyan-500/60 transition-all duration-1000" style={{ width: `${dim.score}%` }} />
                  </div>
                  <p className="text-[8px] text-slate-500 mt-1">Weight: {(dim.weight * 100)}%</p>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      ) : ciiLoading ? (
        <SectionSkeleton rows={6} height="h-32" />
      ) : null}

      {/* International Bodies — UN System Research */}
      <Reveal delay={150}>
        <InternationalBodies />
      </Reveal>

      {/* Data Source Info */}
      <Reveal delay={100}>
        <div className="bg-white/[0.01] border border-white/[0.04] rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BookOpen className="w-4 h-4 text-slate-500" />
            <div>
              <p className="text-[10px] text-slate-400">Data sourced from <span className="text-cyan-400">Semantic Scholar</span>, <span className="text-amber-400">arXiv</span>, <span className="text-purple-400">HuggingFace</span>, <span className="text-green-400">SSRN</span>, <span className="text-red-400">Stanford HAI</span>, <span className="text-blue-400">OECD</span>, and the <span className="text-sky-400">UN System</span></p>
              <p className="text-[9px] text-slate-500">
                {stats.sources?.semantic_scholar && `S2: ${stats.sources.semantic_scholar.toLocaleString()}`}
                {stats.sources?.arxiv && ` | arXiv: ${stats.sources.arxiv.toLocaleString()}`}
                {stats.sources?.huggingface && ` | HF: ${stats.sources.huggingface.toLocaleString()}`}
                {stats.sources?.ssrn && ` | SSRN: ${stats.sources.ssrn.toLocaleString()}`}
                {stats.sources?.stanford_hai && ` | HAI: ${stats.sources.stanford_hai.toLocaleString()}`}
                {stats.sources?.oecd && ` | OECD: ${stats.sources.oecd.toLocaleString()}`}
                {stats.unclassified > 0 && ` | ${stats.unclassified.toLocaleString()} awaiting classification`}
              </p>
            </div>
          </div>
        </div>
      </Reveal>
    </div>
  );
};
